import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Upload, Search, Plus, Edit3, Star, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { formatCents, timeAgo } from '@/utils/cn';
import { ProductFormModal } from './ProductFormModal';

const selectCls = 'h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

export function ProductsPage() {
  const [search, setSearch] = useState('');
  const [promoOnly, setPromoOnly] = useState(false);
  const [filterDepartamento, setFilterDepartamento] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['products', search, promoOnly, filterDepartamento, filterCategoria],
    queryFn: () => apiClient.getProducts({
      search: search || undefined,
      promoOnly: promoOnly || undefined,
      departamento: filterDepartamento || undefined,
      categoria: filterCategoria || undefined,
    }).then(r => r.data),
  });

  const { data: departments } = useQuery({
    queryKey: ['product-departments'],
    queryFn: () => apiClient.getProductDepartments().then(r => r.data as string[]),
  });

  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => apiClient.getProductCategories().then(r => r.data as string[]),
  });

  const { data: agentStatus } = useQuery({
    queryKey: ['agent-status'],
    queryFn: () => apiClient.getAgentStatus().then(r => r.data),
    refetchInterval: 30_000,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => apiClient.uploadProducts(file).then(r => r.data),
    onSuccess: (res: { inserted: number; updated: number; errors: string[]; format?: string }) => {
      const fmt = res.format === 'toledo_mgv4' ? ' (Toledo MGV4)' : res.format === 'csv' ? ' (CSV)' : '';
      toast.success(`${res.inserted} inseridos, ${res.updated} atualizados${fmt}`);
      if (res.errors?.length) {
        toast.warning(`${res.errors.length} linha(s) com erro`, {
          description: res.errors.slice(0, 3).join('\n'),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['agent-status'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      if (msg) toast.error(`Falha no upload: ${msg}`);
    },
  });

  const isOnline = agentStatus?.is_online;
  const activeFilters = [filterDepartamento, filterCategoria, promoOnly ? 'promo' : ''].filter(Boolean).length;

  return (
    <div className="container-app py-6">

      {/* Cabeçalho */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />Produtos
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cadastre manualmente, importe CSV ou sincronize via Agent
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            loading={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />Importar CSV / Toledo
          </Button>
          <Button onClick={() => setCreating(true)} variant="gradient" size="sm" className="shadow-md">
            <Plus className="h-4 w-4" />Novo produto
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMutation.mutate(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Agent status — barra compacta */}
      <div className="flex items-center gap-4 px-4 py-2.5 mb-5 rounded-xl border border-border bg-muted/30 text-sm">
        <span className="font-medium text-muted-foreground">Signage Agent</span>
        <span className={`flex items-center gap-1.5 font-semibold ${isOnline ? 'text-emerald-600' : 'text-red-500'}`}>
          {isOnline ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {isOnline ? 'Online' : 'Offline'}
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <RefreshCw className="h-3 w-3" />
          {timeAgo(agentStatus?.last_full_sync_at) ?? '-'}
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">{agentStatus?.products_count ?? 0}</span> produtos
        </span>
        {agentStatus?.agent_version && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">v{agentStatus.agent_version}</span>
          </>
        )}
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou descrição..."
            className="pl-9 h-9"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select
          value={filterDepartamento}
          disabled={!departments?.length}
          onChange={e => { setFilterDepartamento(e.target.value); setFilterCategoria(''); }}
          className={selectCls}
        >
          <option value="">Todos os departamentos</option>
          {departments?.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        <select
          value={filterCategoria}
          disabled={!categories?.length}
          onChange={e => setFilterCategoria(e.target.value)}
          className={selectCls}
        >
          <option value="">Todas as categorias</option>
          {categories?.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded"
            checked={promoOnly}
            onChange={e => setPromoOnly(e.target.checked)}
          />
          Apenas promoções
        </label>

        {activeFilters > 0 && (
          <button
            onClick={() => { setFilterDepartamento(''); setFilterCategoria(''); setPromoOnly(false); setSearch(''); }}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Limpar filtros ({activeFilters})
          </button>
        )}
      </div>

      {/* Tabela */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando…</div>
      ) : data?.items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/30 p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-brand-600" />
          <h3 className="mt-4 text-xl font-bold">
            {activeFilters > 0 ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}
          </h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {activeFilters > 0
              ? 'Tente ajustar os filtros para ver mais resultados.'
              : 'Cadastre seu primeiro produto manualmente ou importe um CSV/arquivo de balança.'}
          </p>
          {activeFilters > 0 ? (
            <Button className="mt-6" variant="outline" onClick={() => { setFilterDepartamento(''); setFilterCategoria(''); setPromoOnly(false); setSearch(''); }}>
              Limpar filtros
            </Button>
          ) : (
            <div className="mt-6 flex justify-center gap-2">
              <Button onClick={() => setCreating(true)} variant="gradient">
                <Plus className="h-4 w-4" /> Novo produto
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                <Upload className="h-4 w-4" /> Importar CSV
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/30">
                <tr className="text-left">
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase w-12"></th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase w-28">Código</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Descrição</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase w-36">Departamento</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase w-32">Categoria</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase text-right w-28">Preço</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase text-right w-28">Promo</th>
                  <th className="px-3 py-2.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((p: any) => (
                  <tr
                    key={p.codigo}
                    onClick={() => setEditingProduct(p)}
                    className="border-b border-border/50 hover:bg-brand-50/40 cursor-pointer group transition-colors"
                  >
                    <td className="px-3 py-2 text-center">
                      {p.imagem_url ? (
                        <img src={p.imagem_url} alt="" className="h-9 w-9 object-contain inline-block rounded" />
                      ) : (
                        <div className="h-9 w-9 rounded bg-muted text-muted-foreground grid place-items-center text-xs mx-auto">
                          —
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {p.em_destaque && <Star className="inline h-3 w-3 mr-1 text-amber-500 fill-amber-500" />}
                      {p.codigo}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium leading-snug">{p.descricao}</div>
                      {p.marca && <div className="text-xs text-muted-foreground">{p.marca}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {p.departamento
                        ? <span className="text-xs font-medium bg-muted rounded-md px-2 py-0.5">{p.departamento}</span>
                        : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">{p.categoria || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{formatCents(p.preco_cents)}</td>
                    <td className="px-3 py-2 text-right">
                      {p.preco_promo_cents
                        ? <Badge variant="danger" className="tabular-nums">{formatCents(p.preco_promo_cents)}</Badge>
                        : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Edit3 className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 text-xs text-muted-foreground border-t border-border flex items-center justify-between">
            <span>
              {data?.items.length === data?.total
                ? `${data?.total} produto${data?.total !== 1 ? 's' : ''}`
                : `Mostrando ${data?.items.length} de ${data?.total} produtos`}
            </span>
            <span>Clique em uma linha para editar</span>
          </div>
        </Card>
      )}

      {creating && <ProductFormModal product={null} onClose={() => setCreating(false)} />}
      {editingProduct && <ProductFormModal product={editingProduct} onClose={() => setEditingProduct(null)} />}
    </div>
  );
}
